if (!customElements.get('acmer-newsroom')) {
  customElements.define('acmer-newsroom', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.featured = this.querySelector('.newsroom__featured');
      this.initCarousel(options);
      this.search = this.querySelector('.newsroom__heading-row input[type="search"]');
      this.initSearch(options);
    }

    initSearch(options) {
      this.panel = this.querySelector('.newsroom__search-panel');
      this.panelInput = this.querySelector('[data-panel-search]');
      this.panelClear = this.querySelector('.newsroom__panel-clear');
      this.panelInput.classList.add('newsroom__custom-clear');
      this.panelClear.hidden = !this.panelInput.value;
      this.panelClear.addEventListener('click', () => {
        this.panelInput.value = '';
        this.panelInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.panelInput.focus({ preventScroll: true });
      }, options);
      this.searchToggle = this.querySelector('.newsroom__search-toggle');
      this.searchDialog = this.querySelector('.newsroom__search-dialog');
      this.desktopSearch = window.matchMedia('(min-width: 990px)');
      this.searchToggle.hidden = false;
      this.searchToggle.addEventListener('click', () => {
        if (this.panel.hidden) this.openSearchPanel();
        else this.closeSearchPanel(true);
      }, options);
      this.querySelector('.newsroom__search-close').addEventListener('click', () => this.closeSearchPanel(true), options);
      this.searchDialog.addEventListener('cancel', event => { event.preventDefault(); this.closeSearchPanel(true); }, options);
      this.searchDialog.addEventListener('close', () => {
        if (!this.searchDialog.open && this.panel.parentElement === this.searchDialog) this.closeSearchPanel(false);
      }, options);
      this.searchDialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.searchDialog.querySelectorAll('button, input:not([type="hidden"]), a[href]')].filter(element => !element.disabled && element.getClientRects().length);
        const target = event.shiftKey && document.activeElement === focusable[0] ? focusable.at(-1) : !event.shiftKey && document.activeElement === focusable.at(-1) ? focusable[0] : null;
        if (target) { event.preventDefault(); target.focus(); }
      }, options);
      [this.search, this.panelInput].forEach(input => input.addEventListener('input', () => {
        this.search.value = this.panelInput.value = input.value;
        this.panelClear.hidden = !input.value;
      }, options));
      this.panel.querySelectorAll('a').forEach(link => link.addEventListener('click', () => this.closeSearchPanel(false), options));
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || this.panel.hidden) return;
        event.preventDefault();
        this.closeSearchPanel(true);
      }, options);
      const closeOutside = event => {
        if (this.desktopSearch.matches && !this.panel.hidden && !this.panel.contains(event.target) && !this.searchToggle.contains(event.target)) {
          this.closeSearchPanel(event.type === 'pointerdown' && this.panel.contains(document.activeElement));
        }
      };
      document.addEventListener('pointerdown', closeOutside, options);
      document.addEventListener('focusin', closeOutside, options);
      this.desktopSearch.addEventListener('change', () => {
        const restoreFocus = this.panel.contains(document.activeElement);
        this.closeSearchPanel(restoreFocus);
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const link = [...this.querySelectorAll('[data-search-block-id]')].find(item => item.dataset.searchBlockId === event.detail.blockId);
        if (link) this.openSearchPanel();
      }, options);
      this.headerElements = [...document.querySelectorAll('.shopify-section-group-header-group, .hdt-main-header')];
      const updatePosition = () => {
        if (!this.desktopSearch.matches || this.panel.hidden || this.positionFrame) return;
        this.positionFrame = requestAnimationFrame(() => {
          this.positionFrame = null;
          this.positionSearchPanel();
        });
      };
      window.addEventListener('scroll', updatePosition, { ...options, passive: true });
      window.addEventListener('resize', updatePosition, options);
      this.headerObserver = new ResizeObserver(updatePosition);
      this.headerElements.forEach(header => {
        this.headerObserver.observe(header);
        header.addEventListener('transitionend', updatePosition, options);
      });
    }

    positionSearchPanel() {
      const bottom = this.headerElements.reduce((offset, header) => {
        const bounds = header.getBoundingClientRect();
        return bounds.width && bounds.height && bounds.top < window.innerHeight ? Math.max(offset, bounds.bottom) : offset;
      }, 0);
      this.panel.style.setProperty('--newsroom-search-top', `${Math.max(0, bottom)}px`);
    }

    openSearchPanel() {
      if (!this.panel.hidden) return;
      this.setCarouselPaused('search', true);
      this.panelInput.value = this.search.value;
      this.panelClear.hidden = !this.panelInput.value;
      this.panel.hidden = false;
      if (this.desktopSearch.matches) {
        this.positionSearchPanel();
      } else {
        this.searchDialog.append(this.panel);
        this.searchDialog.showModal();
        this.searchScrollStyles = [document.documentElement.style.overflow, document.body.style.overflow];
        document.documentElement.style.overflow = document.body.style.overflow = 'hidden';
      }
      this.searchToggle.setAttribute('aria-expanded', 'true');
      this.panelInput.focus({ preventScroll: true });
    }

    closeSearchPanel(restoreFocus = false) {
      if (!this.panel) return;
      this.setCarouselPaused('search', false);
      this.panel.hidden = true;
      if (this.searchDialog?.open) this.searchDialog.close();
      if (this.panel.parentElement === this.searchDialog) this.prepend(this.panel);
      if (this.searchScrollStyles) {
        [document.documentElement.style.overflow, document.body.style.overflow] = this.searchScrollStyles;
        this.searchScrollStyles = null;
      }
      this.searchToggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.searchToggle.focus({ preventScroll: true });
    }

    initCarousel(options) {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-slide-index]')];
      this.slideIndex = 0;
      this.track = this.querySelector('.newsroom__slides');
      this.carouselReady = !!this.track && this.slides.length > 1;
      this.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.setCarouselPaused('editor', true);
        this.showSlide(index, { animate: false });
      }, options);
      this.addEventListener('shopify:block:deselect', () => this.setCarouselPaused('editor', false), options);
      if (!this.carouselReady) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.carouselPauses = new Set();
      this.carouselElapsed = 0;
      this.autoplay = this.featured.dataset.autoplay !== 'false';
      this.carouselInterval = Number(this.featured.dataset.interval) || 5000;
      this.slides.forEach(slide => { slide.hidden = false; });
      const cloneSlide = slide => {
        const clone = slide.cloneNode(true);
        clone.removeAttribute('data-slide');
        clone.removeAttribute('data-block-id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.dataset.carouselClone = '';
        clone.inert = true;
        clone.setAttribute('aria-hidden', 'true');
        [clone, ...clone.querySelectorAll('[id]')].forEach(element => element.removeAttribute('id'));
        return clone;
      };
      this.track.prepend(cloneSlide(this.slides.at(-1)));
      this.track.append(cloneSlide(this.slides[0]));
      this.track.classList.add('newsroom__slides--ready');
      this.physicalIndex = 1;
      this.track.addEventListener('transitionend', event => {
        if (event.target === this.track && event.propertyName === 'transform') this.finishCarouselMove();
      }, options);
      this.carouselResize = new ResizeObserver(() => {
        this.cancelCarouselDrag?.();
        this.finishCarouselMove();
      });
      this.carouselResize.observe(this.track);

      const controls = this.querySelector('.newsroom__carousel-controls');
      controls.hidden = false;
      this.querySelector('[data-previous]').addEventListener('click', () => this.stepSlide(-1), options);
      this.querySelector('[data-next]').addEventListener('click', () => this.stepSlide(1), options);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.showSlide(index), options));
      controls.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') this.showSlide(event.key === 'Home' ? 0 : this.slides.length - 1);
        else this.stepSlide(event.key === 'ArrowRight' ? 1 : -1);
        this.dots[this.slideIndex].focus();
      }, options);
      this.featured.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') this.setCarouselPaused('hover', true);
      }, options);
      this.featured.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse') this.setCarouselPaused('hover', false);
      }, options);
      this.featured.addEventListener('focusin', () => this.setCarouselPaused('focus', true), options);
      this.featured.addEventListener('focusout', event => {
        if (!this.featured.contains(event.relatedTarget)) this.setCarouselPaused('focus', false);
      }, options);
      document.addEventListener('visibilitychange', () => this.setCarouselPaused('hidden', document.hidden), options);
      const applyMotionPreference = () => {
        this.setCarouselPaused('reduced', this.reducedMotion.matches);
        if (this.reducedMotion.matches) this.finishCarouselMove();
      };
      this.reducedMotion.addEventListener('change', applyMotionPreference, options);
      this.carouselVisibility = new IntersectionObserver(entries => {
        this.setCarouselPaused('offscreen', !entries[0].isIntersecting);
      });
      this.carouselVisibility.observe(this.featured);
      this.initSwipe(options);
      this.showSlide(0, { animate: false });
      this.setCarouselPaused('hidden', document.hidden);
      applyMotionPreference();
    }

    paintCarousel(offset = 0, animate = false) {
      this.track.classList.toggle('newsroom__slides--moving', animate && !this.reducedMotion.matches);
      this.track.style.transform = `translate3d(calc(${-this.physicalIndex * 100}% + ${offset}px), 0, 0)`;
    }

    finishCarouselMove() {
      if (!this.carouselReady) return;
      clearTimeout(this.carouselMoveTimer);
      this.physicalIndex = this.slideIndex + 1;
      this.paintCarousel();
    }

    stepSlide(direction) {
      this.finishCarouselMove();
      this.showSlide(this.slideIndex + direction, { physical: this.physicalIndex + direction });
    }

    showSlide(index, { animate = true, physical = null, resetProgress = true } = {}) {
      if (!this.slides.length) return;
      this.finishCarouselMove();
      // Commit the loop-edge jump before starting the next transition.
      if (this.carouselReady) this.track.getBoundingClientRect();
      this.slideIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        slide.hidden = !this.carouselReady && i !== this.slideIndex;
        slide.inert = i !== this.slideIndex;
        slide.setAttribute('aria-hidden', String(i !== this.slideIndex));
      });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === this.slideIndex)));
      if (this.carouselReady) {
        this.physicalIndex = physical ?? this.slideIndex + 1;
        this.paintCarousel(0, animate);
        this.carouselMoveTimer = setTimeout(() => this.finishCarouselMove(), 450);
        if (resetProgress) this.carouselElapsed = 0;
      }
      const status = this.querySelector('[data-slide-status]');
      if (status) status.textContent = `Featured article ${this.slideIndex + 1} of ${this.slides.length}`;
    }

    setCarouselPaused(reason, paused) {
      if (!this.carouselReady || !this.carouselPauses) return;
      if (paused) this.carouselPauses.add(reason);
      else this.carouselPauses.delete(reason);
      cancelAnimationFrame(this.carouselFrame);
      this.carouselFrame = null;
      const running = this.autoplay && this.carouselPauses.size === 0;
      this.querySelector('[data-slide-status]').setAttribute('aria-live', running ? 'off' : 'polite');
      if (!running) return;
      let previous = performance.now();
      const tick = now => {
        this.carouselElapsed += now - previous;
        previous = now;
        if (this.carouselElapsed >= this.carouselInterval) this.stepSlide(1);
        this.carouselFrame = requestAnimationFrame(tick);
      };
      this.carouselFrame = requestAnimationFrame(tick);
    }

    initSwipe(options) {
      const track = this.track;
      let gesture = null;
      let suppressClickUntil = 0;
      const begin = (x, y, kind) => {
        this.finishCarouselMove();
        gesture = { x, y, kind, offset: 0, horizontal: false, started: performance.now() };
        suppressClickUntil = 0;
        this.setCarouselPaused('drag', true);
      };
      const move = (x, y) => {
        if (!gesture) return;
        const dx = x - gesture.x, dy = y - gesture.y;
        if (!gesture.horizontal && Math.max(Math.abs(dx), Math.abs(dy)) >= 8) {
          if (Math.abs(dx) <= Math.abs(dy) * 1.3) { end(true); return; }
          gesture.horizontal = true;
          track.classList.add('newsroom__slides--dragging');
        }
        if (gesture.horizontal) {
          gesture.offset = Math.max(-track.clientWidth, Math.min(track.clientWidth, dx));
          this.paintCarousel(gesture.offset);
        }
      };
      const end = (canceled = false) => {
        if (!gesture) return;
        const { offset, horizontal, started } = gesture;
        gesture = null;
        track.classList.remove('newsroom__slides--dragging');
        if (horizontal) {
          suppressClickUntil = performance.now() + 500;
          const enough = Math.abs(offset) >= Math.max(40, track.clientWidth * .2);
          const flick = Math.abs(offset) >= 20 && performance.now() - started < 250;
          const direction = !canceled && (enough || flick) ? (offset < 0 ? 1 : -1) : 0;
          // Keep the dragged position as the animation's starting point.
          const physical = this.slideIndex + 1 + direction;
          clearTimeout(this.carouselMoveTimer);
          this.slideIndex = ((this.slideIndex + direction) % this.slides.length + this.slides.length) % this.slides.length;
          this.physicalIndex = physical;
          this.paintCarousel(0, true);
          this.slides.forEach((slide, i) => { slide.inert = i !== this.slideIndex; slide.setAttribute('aria-hidden', String(i !== this.slideIndex)); });
          this.dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === this.slideIndex)));
          this.querySelector('[data-slide-status]').textContent = `Featured article ${this.slideIndex + 1} of ${this.slides.length}`;
          if (direction) this.carouselElapsed = 0;
          this.carouselMoveTimer = setTimeout(() => this.finishCarouselMove(), 450);
        }
        this.setCarouselPaused('drag', false);
      };
      this.cancelCarouselDrag = () => end(true);
      track.addEventListener('touchstart', event => {
        if (event.touches.length !== 1) { end(true); return; }
        begin(event.touches[0].clientX, event.touches[0].clientY, 'touch');
      }, { ...options, passive: true });
      track.addEventListener('touchmove', event => {
        if (gesture?.kind !== 'touch') return;
        if (event.touches.length !== 1) { end(true); return; }
        move(event.touches[0].clientX, event.touches[0].clientY);
        if (gesture?.horizontal && event.cancelable) event.preventDefault();
      }, { ...options, passive: false });
      track.addEventListener('touchend', () => end(), { ...options, passive: true });
      track.addEventListener('touchcancel', () => end(true), { ...options, passive: true });
      track.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse' && event.button === 0) begin(event.clientX, event.clientY, 'mouse');
      }, options);
      window.addEventListener('pointermove', event => {
        if (gesture?.kind === 'mouse') move(event.clientX, event.clientY);
      }, options);
      window.addEventListener('pointerup', () => { if (gesture?.kind === 'mouse') end(); }, options);
      window.addEventListener('pointercancel', () => { if (gesture?.kind === 'mouse') end(true); }, options);
      window.addEventListener('blur', () => end(true), options);
      track.addEventListener('dragstart', event => event.preventDefault(), options);
      track.addEventListener('click', event => {
        if (event.detail && performance.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { ...options, capture: true });
    }

    disconnectedCallback() {
      this.carouselReady = false;
      cancelAnimationFrame(this.carouselFrame);
      clearTimeout(this.carouselMoveTimer);
      this.carouselResize?.disconnect();
      this.carouselVisibility?.disconnect();
      this.track?.querySelectorAll('[data-carousel-clone]').forEach(clone => clone.remove());
      if (this.track) {
        this.track.classList.remove('newsroom__slides--ready', 'newsroom__slides--moving', 'newsroom__slides--dragging');
        this.track.style.removeProperty('transform');
      }
      this.slides?.forEach((slide, i) => { slide.hidden = i !== 0; slide.inert = false; slide.removeAttribute('aria-hidden'); });
      this.controller?.abort();
      this.controller = null;
      this.headerObserver?.disconnect();
      cancelAnimationFrame(this.positionFrame);
      this.positionFrame = null;
      this.closeSearchPanel();
    }

  });
}
